import type {
  DesktopPraxaResourcesBridge,
  PraxaDesktopChannelHistory,
  PraxaDesktopConversationHistory,
  PraxaDesktopHistoryInvalidation,
  PraxaDesktopResourceIndexState,
} from "@praxa/platform-contracts";
import { useEffect, useRef, useState } from "react";

export const PRAXA_HISTORY_LOAD_ERROR =
  "This Praxa history couldn’t load. Try again when the cloud connection is available.";
export const PRAXA_HISTORY_REFRESH_REQUIRED_ERROR =
  "New activity couldn’t be refreshed. Showing saved messages; try again to check for updates.";

export type PraxaHistorySelection =
  | {
      readonly kind: "conversation";
      readonly conversationId: string;
    }
  | {
      readonly kind: "channel";
      readonly workspaceId: string;
      readonly channelId: string;
    };

export type PraxaDesktopHistory = PraxaDesktopConversationHistory | PraxaDesktopChannelHistory;

export interface PraxaHistoryLoadResult {
  readonly authoritative: boolean;
  readonly history: PraxaDesktopHistory | null;
}

export interface PraxaHistoryAuthority {
  readonly authorized: boolean;
  readonly revision: number;
}

export interface PraxaHistoryRequestState {
  readonly requestKey: string | null;
  readonly selectionKey: string | null;
  readonly error: string | null;
  readonly history: PraxaDesktopHistory | null;
  readonly loading: boolean;
}

interface PraxaHistoryReloadRequest {
  readonly authorityRevision: number;
  readonly mode: "cache" | "network";
  readonly selectionKey: string;
  readonly sequence: number;
}

export function isPraxaHistorySelectionAuthorized(
  state: PraxaDesktopResourceIndexState | null,
  selection: PraxaHistorySelection,
  routeWorkspaceId: string,
): boolean {
  if (state === null || state.workspaceId === null || state.workspaceKind === null) return false;
  if (selection.kind === "conversation") {
    return (
      routeWorkspaceId === "personal" &&
      state.workspaceKind === "personal" &&
      state.workspaceId === "personal" &&
      state.conversations.some(
        (conversation) => conversation.conversationId === selection.conversationId,
      )
    );
  }
  return (
    routeWorkspaceId === selection.workspaceId &&
    state.workspaceKind === "organization" &&
    state.workspaceId === selection.workspaceId &&
    state.channels.some(
      (channel) =>
        channel.workspaceId === selection.workspaceId && channel.channelId === selection.channelId,
    )
  );
}

export function praxaHistoryRequestKey(
  selection: PraxaHistorySelection,
  authorityRevision: number,
  retryRequest: number,
): string {
  const selectionKey =
    selection.kind === "conversation"
      ? `conversation:${selection.conversationId}`
      : `channel:${selection.workspaceId}:${selection.channelId}`;
  return `${authorityRevision}:${retryRequest}:${selectionKey}`;
}

export function doesPraxaHistoryInvalidationMatchSelection(
  invalidation: PraxaDesktopHistoryInvalidation,
  selection: PraxaHistorySelection,
): boolean {
  return invalidation.kind === "conversation"
    ? selection.kind === "conversation" && invalidation.conversationId === selection.conversationId
    : selection.kind === "channel" &&
        invalidation.workspaceId === selection.workspaceId &&
        invalidation.channelId === selection.channelId;
}

export function subscribeToPraxaHistoryInvalidations(
  bridge: DesktopPraxaResourcesBridge,
  selection: PraxaHistorySelection,
  onMatchingInvalidation: () => void,
): () => void {
  const candidate = bridge as Partial<DesktopPraxaResourcesBridge>;
  if (typeof candidate.onHistoryInvalidation !== "function") return () => {};
  return candidate.onHistoryInvalidation((invalidation) => {
    if (doesPraxaHistoryInvalidationMatchSelection(invalidation, selection)) {
      onMatchingInvalidation();
    }
  });
}

export function subscribeToPraxaHistoryRefreshRequirements(
  bridge: DesktopPraxaResourcesBridge,
  selection: PraxaHistorySelection,
  onMatchingRequirement: () => void,
): () => void {
  const candidate = bridge as Partial<DesktopPraxaResourcesBridge>;
  if (typeof candidate.onHistoryRefreshRequired !== "function") return () => {};
  return candidate.onHistoryRefreshRequired((invalidation) => {
    if (doesPraxaHistoryInvalidationMatchSelection(invalidation, selection)) {
      onMatchingRequirement();
    }
  });
}

function getBridge(): DesktopPraxaResourcesBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaResources;
}

export async function loadPraxaHistory(
  bridge: DesktopPraxaResourcesBridge,
  selection: PraxaHistorySelection,
): Promise<PraxaHistoryLoadResult> {
  return selection.kind === "conversation"
    ? bridge.loadConversationHistory(selection.conversationId)
    : bridge.loadChannelHistory(selection.workspaceId, selection.channelId);
}

export async function loadCachedPraxaHistory(
  bridge: DesktopPraxaResourcesBridge,
  selection: PraxaHistorySelection,
): Promise<PraxaHistoryLoadResult> {
  if (selection.kind === "conversation") {
    const history =
      typeof bridge.readCachedConversationHistory === "function"
        ? await bridge.readCachedConversationHistory(selection.conversationId)
        : null;
    return { authoritative: history !== null, history };
  }
  const history =
    typeof bridge.readCachedChannelHistory === "function"
      ? await bridge.readCachedChannelHistory(selection.workspaceId, selection.channelId)
      : null;
  return { authoritative: history !== null, history };
}

function historyRequestError(history: PraxaDesktopHistory | null): string {
  return history === null ? PRAXA_HISTORY_LOAD_ERROR : PRAXA_HISTORY_REFRESH_REQUIRED_ERROR;
}

export function beginPraxaHistoryRequest(
  current: PraxaHistoryRequestState,
  requestKey: string,
  selectionKey: string,
): PraxaHistoryRequestState {
  const retainCurrent = current.selectionKey === selectionKey;
  return {
    requestKey,
    selectionKey,
    error: retainCurrent && current.history !== null ? current.error : null,
    history: retainCurrent ? current.history : null,
    loading: true,
  };
}

export function completePraxaHistoryRequest(
  current: PraxaHistoryRequestState,
  requestKey: string,
  selectionKey: string,
  result: PraxaHistoryLoadResult,
): PraxaHistoryRequestState {
  const retainedHistory = current.selectionKey === selectionKey ? current.history : null;
  const history = result.history ?? retainedHistory;
  return {
    requestKey,
    selectionKey,
    error: result.authoritative && result.history !== null ? null : historyRequestError(history),
    history,
    loading: false,
  };
}

export function failPraxaHistoryRequest(
  current: PraxaHistoryRequestState,
  requestKey: string,
  selectionKey: string,
): PraxaHistoryRequestState {
  const history = current.selectionKey === selectionKey ? current.history : null;
  return {
    requestKey,
    selectionKey,
    error: historyRequestError(history),
    history,
    loading: false,
  };
}

export function useDesktopPraxaHistory(
  selection: PraxaHistorySelection,
  authority: PraxaHistoryAuthority,
): {
  readonly error: string | null;
  readonly history: PraxaDesktopHistory | null;
  readonly loading: boolean;
  readonly retry: () => void;
} {
  const bridge = getBridge();
  const [requestState, setRequestState] = useState<PraxaHistoryRequestState>({
    requestKey: null,
    selectionKey: null,
    error: null,
    history: null,
    loading: false,
  });
  const requestVersionRef = useRef(0);
  const selectionKey = praxaHistoryRequestKey(selection, 0, 0);
  const [reloadRequest, setReloadRequest] = useState<PraxaHistoryReloadRequest>({
    authorityRevision: authority.revision,
    mode: "network",
    selectionKey,
    sequence: 0,
  });
  const requestKey = praxaHistoryRequestKey(selection, authority.revision, reloadRequest.sequence);
  const requestMode =
    reloadRequest.authorityRevision === authority.revision &&
    reloadRequest.selectionKey === selectionKey
      ? reloadRequest.mode
      : "network";

  useEffect(() => {
    if (!bridge || !authority.authorized) return;
    return subscribeToPraxaHistoryInvalidations(bridge, selection, () => {
      setReloadRequest((request) => ({
        authorityRevision: authority.revision,
        mode: "cache",
        selectionKey,
        sequence: request.sequence + 1,
      }));
    });
  }, [authority.authorized, authority.revision, bridge, selectionKey]);

  useEffect(() => {
    if (!bridge || !authority.authorized) return;
    return subscribeToPraxaHistoryRefreshRequirements(bridge, selection, () => {
      requestVersionRef.current += 1;
      setRequestState((current) => {
        const history = current.selectionKey === selectionKey ? current.history : null;
        return {
          requestKey,
          selectionKey,
          error: historyRequestError(history),
          history,
          loading: false,
        };
      });
    });
  }, [authority.authorized, bridge, requestKey, selectionKey]);

  useEffect(() => {
    const requestVersion = ++requestVersionRef.current;
    if (!bridge || !authority.authorized) {
      setRequestState({
        requestKey,
        selectionKey,
        error: bridge ? null : PRAXA_HISTORY_LOAD_ERROR,
        history: null,
        loading: false,
      });
      return;
    }

    setRequestState((current) => beginPraxaHistoryRequest(current, requestKey, selectionKey));
    const load =
      requestMode === "cache"
        ? loadCachedPraxaHistory(bridge, selection)
        : loadPraxaHistory(bridge, selection);
    void load
      .then((result) => {
        if (requestVersionRef.current !== requestVersion) return;
        setRequestState((current) =>
          completePraxaHistoryRequest(current, requestKey, selectionKey, result),
        );
      })
      .catch(() => {
        if (requestVersionRef.current === requestVersion) {
          setRequestState((current) => failPraxaHistoryRequest(current, requestKey, selectionKey));
        }
      });

    return () => {
      requestVersionRef.current += 1;
    };
  }, [authority.authorized, bridge, requestKey, requestMode]);

  const retry = () =>
    setReloadRequest((request) => ({
      authorityRevision: authority.revision,
      mode: "network",
      selectionKey,
      sequence: request.sequence + 1,
    }));
  if (!authority.authorized) {
    return { error: null, history: null, loading: false, retry };
  }
  if (!bridge) {
    return {
      error: PRAXA_HISTORY_LOAD_ERROR,
      history: null,
      loading: false,
      retry,
    };
  }
  if (requestState.requestKey !== requestKey) {
    if (requestState.selectionKey === selectionKey && requestState.history !== null) {
      return {
        error: requestState.error,
        history: requestState.history,
        loading: true,
        retry,
      };
    }
    return { error: null, history: null, loading: true, retry };
  }

  return {
    error: requestState.error,
    history: requestState.history,
    loading: requestState.loading,
    retry,
  };
}
